"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Table, Button, Badge, Alert } from "reactstrap"
import { FaPlus, FaEdit, FaTrash, FaCar, FaPhone, FaIdCard } from "react-icons/fa"
import { toast } from "react-toastify"
import { StaffCarService } from "../../../../Service/StaffCarService"
import type { Car, TokenData, PaginatedResponse } from "./types"

interface Props {
  token: string
  tokenData: TokenData
  onAdd: () => void
  onEdit: (item: Car) => void
  refresh: boolean
}

const CarTab: React.FC<Props> = ({ token, tokenData, onAdd, onEdit, refresh }) => {
  const [cars, setCars] = useState<Car[]>([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)

  const fetchCars = async (page = 1) => {
    try {
      setLoading(true)
      const response: PaginatedResponse<Car> = await StaffCarService.getCars(token, page, 10)
      setCars(response.results)
      setTotalPages(response.total_pages)
      setTotalCount(response.count)
      setCurrentPage(page)
    } catch (error) {
      console.error("Failed to fetch cars:", error)
      toast.error("Failed to load car data")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCars(currentPage)
  }, [token, refresh])

  const handleDelete = async (id: number) => {
    if (window.confirm("Are you sure you want to delete this car?")) {
      try {
        await StaffCarService.deleteCar(token, id)
        toast.success("Car deleted successfully")
        fetchCars(currentPage)
      } catch (error) {
        console.error("Failed to delete car:", error)
        toast.error("Failed to delete car")
      }
    }
  }

  const canAddMore = totalCount < tokenData.max_cars

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" style={{ width: "2.5rem", height: "2.5rem" }} role="status">
          <span className="visually-hidden">Loading vehicles...</span>
        </div>
        <p className="mt-3 text-muted">Loading vehicle information...</p>
      </div>
    )
  }

  return (
    <div>
      <style >{`
        .professional-header {
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 24px;
          border: 1px solid #e2e8f0;
        }
        
        .add-btn {
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          border: none;
          border-radius: 8px;
          padding: 8px 16px;
          font-weight: 500;
          font-size: 0.875rem;
          transition: all 0.3s ease;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
        }
        
        .add-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
        }
        
        .add-btn:disabled {
          background: #e2e8f0;
          color: #64748b;
          box-shadow: none;
        }
        
        .professional-table {
          border: none;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
        }
        
        .professional-table thead th {
          background: #f8fafc;
          border: none;
          font-weight: 600;
          color: #475569;
          font-size: 0.8rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          padding: 16px 12px;
        }
        
        .professional-table tbody td {
          border: none;
          padding: 16px 12px;
          vertical-align: middle;
          font-size: 0.875rem;
        }
        
        .professional-table tbody tr {
          border-bottom: 1px solid #f1f5f9;
          transition: all 0.2s ease;
        }
        
        .professional-table tbody tr:hover {
          background: #f8fafc;
        }
        
        .action-btn {
          border-radius: 6px;
          padding: 6px 10px;
          font-size: 0.75rem;
          border: 1px solid;
          transition: all 0.2s ease;
        }
        
        .action-btn:hover {
          transform: translateY(-1px);
        }
        
        .edit-btn {
          color: #3b82f6;
          border-color: #3b82f6;
          background: rgba(59, 130, 246, 0.1);
        }
        
        .edit-btn:hover {
          background: #3b82f6;
          color: white;
        }
        
        .delete-btn {
          color: #ef4444;
          border-color: #ef4444;
          background: rgba(239, 68, 68, 0.1);
        }
        
        .delete-btn:hover {
          background: #ef4444;
          color: white;
        }
        
        .status-badge {
          font-size: 0.75rem;
          padding: 4px 10px;
          border-radius: 20px;
          font-weight: 500;
        }
        
        .plate-number {
          font-family: 'Courier New', monospace;
          font-weight: bold;
          color: #1e293b;
          background: #f1f5f9;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 0.8rem;
        }
        
        .driver-info {
          display: flex;
          align-items: center;
          gap: 6px;
          color: #64748b;
        }
        
        .pagination-modern .page-link {
          border: none;
          border-radius: 6px;
          margin: 0 2px;
          padding: 8px 12px;
          color: #64748b;
          font-weight: 500;
          transition: all 0.2s ease;
        }
        
        .pagination-modern .page-item.active .page-link {
          background: #3b82f6;
          color: white;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
        }
        
        .pagination-modern .page-link:hover {
          background: #f1f5f9;
          color: #3b82f6;
        }
        
        .empty-state {
          text-align: center;
          padding: 48px 24px;
          background: #f8fafc;
          border-radius: 12px;
          border: 2px dashed #cbd5e1;
        }
        
        .limit-alert {
          background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
          border: 1px solid #f59e0b;
          border-radius: 8px;
          color: #92400e;
        }
      `}</style>

      <div className="professional-header">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-2 fw-bold text-dark">Vehicle Registry</h5>
            <div className="d-flex align-items-center gap-2">
              <span className="badge bg-primary bg-opacity-10 text-warning px-3 py-2">
                {totalCount} / {tokenData.max_cars} Registered
              </span>
              <span className="text-muted small">{tokenData.max_cars - totalCount} slots remaining</span>
            </div>
          </div>
          <Button
            className="add-btn"
            onClick={onAdd}
            disabled={!canAddMore}
            title={!canAddMore ? "Maximum vehicle limit reached" : "Register new vehicle"}
          >
            <FaPlus className="me-2" size={12} />
            Add Vehicle
          </Button>
        </div>
      </div>

      {!canAddMore && (
        <Alert className="limit-alert mb-4">
          <div className="d-flex align-items-center">
            <FaCar className="me-2" size={16} />
            <strong>Registration Complete:</strong> All {tokenData.max_cars} vehicle slots have been filled.
          </div>
        </Alert>
      )}

      {cars.length === 0 ? (
        <div className="empty-state">
          <FaCar className="mb-3 text-muted" size={48} />
          <h6 className="text-muted mb-2">No Vehicles Registered</h6>
          <p className="text-muted small mb-0">Start by registering your first vehicle for the exhibition.</p>
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <Table className="professional-table mb-0">
              <thead>
                <tr>
                  <th>License Plate</th>
                  <th>Driver Information</th>
                  <th>Contact</th>
                  <th>Status</th>
                  <th>Registration Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {cars.map((car) => (
                  <tr key={car.id}>
                    <td>
                      <span className="plate-number">{car.plate_number}</span>
                    </td>
                    <td>
                      <div className="driver-info">
                        <FaIdCard size={14} />
                        <span className="fw-medium">{car.driver_name}</span>
                      </div>
                    </td>
                    <td>
                      <div className="driver-info">
                        <FaPhone size={12} />
                        <span>{car.driver_phone}</span>
                      </div>
                    </td>
                    <td>
                      <Badge className="status-badge" color={car.status ? "success" : "secondary"}>
                        {car.status ? "Active" : "Inactive"}
                      </Badge>
                    </td>
                    <td className="text-muted small">
                      {car.created_at
                        ? new Date(car.created_at).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })
                        : "N/A"}
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button
                          className="action-btn"
                          size="sm"
                          color="success"
                          onClick={() => onEdit(car)}
                          title="Edit vehicle details"
                        >
                          <FaEdit size={11} />
                        </Button>
                        <Button
                          className="action-btn delete-btn"
                          size="sm"
                          color="danger"
                          onClick={() => handleDelete(car.id)}
                          title="Remove vehicle"
                        >
                          <FaTrash size={11} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-4">
              <nav>
                <ul className="pagination pagination-sm pagination-modern">
                  <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                    <button
                      className="page-link"
                      onClick={() => fetchCars(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                  </li>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let page = i + 1
                    if (totalPages > 5) {
                      if (currentPage > 3) {
                        page = currentPage - 2 + i
                      }
                      if (currentPage > totalPages - 2) {
                        page = totalPages - 4 + i
                      }
                    }
                    return (
                      <li key={page} className={`page-item ${currentPage === page ? "active" : ""}`}>
                        <button className="page-link" onClick={() => fetchCars(page)}>
                          {page}
                        </button>
                      </li>
                    )
                  })}
                  <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                    <button
                      className="page-link"
                      onClick={() => fetchCars(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default CarTab
